import React, { useState } from "react";

/**
 * Expandable CSV import panel (shared platform component).
 *
 * Two input modes:
 *  - File Upload: download sample + `<input type="file">` (original behavior).
 *  - Paste Text:  monospace `<textarea>` for pasting CSV/TSV rows directly (Excel/Sheets)
 *                 + Import button. No file involved.
 *
 * Paste mode is enabled automatically when `onImportText` is provided. When it is omitted
 * the panel renders the original file-only UI (backward compatible).
 *
 * Optional `children` render after the column hint (preview tables, validation, submit).
 */
export default function ImportCsvPanel({
  expanded,
  fileInputId,
  importing,
  columnHint,
  onDownloadSample,
  onFileChange,
  onImportText,
  pastePlaceholder,
  children,
}) {
  const pasteEnabled = typeof onImportText === "function";
  const [mode, setMode] = useState("file");
  const [pasteText, setPasteText] = useState("");

  if (!expanded) return null;

  const isPaste = pasteEnabled && mode === "paste";

  function handleImportText() {
    const text = pasteText.trim();
    if (!text || importing) return;
    onImportText(text);
    setPasteText("");
  }

  return (
    <div className="panel">
      <div className="panel-title">Import CSV</div>

      {pasteEnabled && (
        <div className="csv-import-segmented" role="radiogroup" aria-label="CSV import mode">
          <button
            type="button"
            className="csv-import-segmented__btn"
            role="radio"
            aria-checked={!isPaste}
            onClick={() => setMode("file")}
            disabled={importing}
          >
            File Upload
          </button>
          <button
            type="button"
            className="csv-import-segmented__btn"
            role="radio"
            aria-checked={isPaste}
            onClick={() => setMode("paste")}
            disabled={importing}
          >
            Paste Text / Clipboard
          </button>
        </div>
      )}

      {!isPaste ? (
        <div className="panel-row">
          {typeof onDownloadSample === "function" ? (
            <button type="button" onClick={onDownloadSample} className="btn btn-sm btn-primary-outline">
              Download sample CSV
            </button>
          ) : null}
          <input
            type="file"
            id={fileInputId}
            accept=".csv"
            onChange={onFileChange}
            disabled={importing}
            className="input input-file"
          />
          {importing && <span className="panel-hint">Importing…</span>}
        </div>
      ) : (
        <div className="csv-import-paste">
          <textarea
            className="input csv-import-paste__textarea"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={
              pastePlaceholder ||
              "Paste rows here. First line is the header.\nSupports comma (CSV) or tab-separated (Excel / Google Sheets)."
            }
            spellCheck={false}
            disabled={importing}
            rows={2}
          />
          <div className="panel-row">
            <button
              type="button"
              onClick={handleImportText}
              className="btn btn-sm btn-primary"
              disabled={importing || pasteText.trim() === ""}
            >
              Import
            </button>
            {importing && <span className="panel-hint">Importing…</span>}
          </div>
        </div>
      )}

      {columnHint ? <div className="panel-hint">{columnHint}</div> : null}
      {children}
    </div>
  );
}
